import React, { useState, useRef, useEffect, useContext } from "react";
import { Bell } from "lucide-react";
import { useNotifications } from "../../context/NotificationContext";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import NotificationDropdown from "./NotificationDropdown";
import RemotePunchAlertModal from "../RemotePunch/RemotePunchAlertModal";
import "./notification.css";

// Count increases this soon after mount are the initial fetch catching up,
// not a new arrival — don't ring for them.
const RING_GRACE_MS = 4000;

const NotificationBell = ({ isMobile }) => {
  const {
    unreadCount,
    fetchNotifications,
    fetchUnreadCount,
    isDropdownOpen,
    setIsDropdownOpen,
    notificationsDisabled,
  } = useNotifications();
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const bellRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const lastCountRef = useRef(unreadCount);
  const ringTimerRef = useRef(null);
  const [ringing, setRinging] = useState(false);
  const [remotePunchAlertId, setRemotePunchAlertId] = useState(null);

  const isDark = theme === "dark";
  const isAr = language === "ar";

  // Open instantly with the in-memory list (kept live by the socket) and
  // refresh in the background — the click never waits on the network.
  const toggleDropdown = (e) => {
    e.stopPropagation();
    const opening = !isDropdownOpen;
    setIsDropdownOpen(opening);
    if (opening) {
      fetchNotifications(1, false);
      fetchUnreadCount();
    }
  };

  // Ring the bell when a new unread notification arrives.
  useEffect(() => {
    const previous = lastCountRef.current;
    lastCountRef.current = unreadCount;
    if (unreadCount <= previous) return;
    if (Date.now() - mountedAtRef.current < RING_GRACE_MS) return;
    setRinging(true);
    clearTimeout(ringTimerRef.current);
    ringTimerRef.current = setTimeout(() => setRinging(false), 1000);
  }, [unreadCount]);

  useEffect(() => () => clearTimeout(ringTimerRef.current), []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isDropdownOpen, setIsDropdownOpen]);

  if (notificationsDisabled) {
    return null;
  }

  // Theme-aware button styles — matches the topbar theme-toggle button
  // (sidebarStyles.js themeToggle: c.inputBg / c.inputBorder / c.text)
  // exactly, so the bell reads as one of the same set of buttons.
  const buttonStyle = {
    background: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(248, 250, 252, 0.8)",
    border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
    borderRadius: "0.625rem",
    padding: 0,
    width: isMobile ? "2.375rem" : "2.5rem",
    height: isMobile ? "2.375rem" : "2.5rem",
    flexShrink: 0,
    cursor: "pointer",
    color: isDark ? "#ffffff" : "#0f172a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s ease",
    position: "relative",
  };

  const badgeText = unreadCount > 99 ? "99+" : String(unreadCount);
  const ariaLabel = unreadCount > 0
    ? (isAr ? `الإشعارات، ${badgeText} غير مقروءة` : `Notifications, ${badgeText} unread`)
    : (isAr ? "الإشعارات" : "Notifications");

  return (
    <div className="notification-bell-container" ref={bellRef}>
      <button
        type="button"
        className="notification-bell-btn nx-tb"
        style={buttonStyle}
        onClick={toggleDropdown}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isDropdownOpen}
      >
        <Bell
          size={18}
          className={`notification-bell-icon${ringing ? " is-ringing" : ""}`}
          aria-hidden="true"
        />
        {unreadCount > 0 && (
          <span key={badgeText} className="notification-badge" aria-hidden="true">
            {badgeText}
          </span>
        )}
      </button>

      <NotificationDropdown
        isOpen={isDropdownOpen}
        onClose={() => setIsDropdownOpen(false)}
        isMobile={isMobile}
        onOpenRemotePunchAlert={setRemotePunchAlertId}
      />
      {remotePunchAlertId && (
        <RemotePunchAlertModal
          alertId={remotePunchAlertId}
          theme={theme}
          language={language}
          onClose={() => setRemotePunchAlertId(null)}
        />
      )}
    </div>
  );
};

export default NotificationBell;
